import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Minus, Trash2, AlertTriangle, ChefHat, LogOut, Search, ChevronDown, CheckCircle2, AlertCircle, Info, Save, Pencil, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUploader } from '@/components/ImageUploader';

export default function InventoryPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [ingredients, setIngredients] = useState([]);
  const [lowStock, setLowStock] = useState({ count: 0, items: [] });
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', unit: 'each', stock_quantity: 0, low_stock_threshold: 10, cost_per_unit: 0, supplier: '' });
  const [confirmDelete, setConfirmDelete] = useState(null); // ingredient pending delete

  const load = useCallback(async () => {
    try {
      const [ing, low, mi, cats] = await Promise.all([
        api.get('/inventory/ingredients'),
        api.get('/inventory/low-stock'),
        api.get('/menu/items', { params: { include_unavailable: true } }),
        api.get('/menu/categories'),
      ]);
      setIngredients(ing.data || []);
      setLowStock(low.data || { count: 0, items: [] });
      setMenuItems(mi.data || []);
      setCategories(cats.data || []);
    } catch (e) { toast.error('Failed to load inventory'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post('/inventory/ingredients', { ...form, stock_quantity: Number(form.stock_quantity), low_stock_threshold: Number(form.low_stock_threshold), cost_per_unit: Number(form.cost_per_unit) });
      toast.success('Ingredient added');
      setShowAdd(false); setForm({ name: '', unit: 'each', stock_quantity: 0, low_stock_threshold: 10, cost_per_unit: 0, supplier: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const adjust = async (id, delta) => {
    try { await api.post(`/inventory/ingredients/${id}/adjust`, { delta, reason: 'manual_adjustment' }); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await api.delete(`/inventory/ingredients/${confirmDelete.id}`); load(); toast.success('Removed'); setConfirmDelete(null); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Inventory</h1>
          <p className="text-xs text-muted-foreground">Ingredients & recipes · {user?.full_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => nav('/admin')}>Admin</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6 sm:px-6">
        {lowStock.count > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="flex items-center gap-3 p-4 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5" />
              <span><strong>{lowStock.count}</strong> ingredient{lowStock.count !== 1 ? 's' : ''} at or below low-stock threshold: {lowStock.items.map((i) => i.name).join(', ')}</span>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="ingredients">
          <TabsList>
            <TabsTrigger data-testid="inventory-tab-ingredients" value="ingredients">Ingredients</TabsTrigger>
            <TabsTrigger data-testid="inventory-tab-dishes" value="dishes">Dishes</TabsTrigger>
            <TabsTrigger data-testid="inventory-tab-recipes" value="recipes">Recipes</TabsTrigger>
          </TabsList>
          <TabsContent value="ingredients">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle>Ingredients</CardTitle>
                  <p className="mt-1 text-xs text-muted-foreground">Track stock per unit. Inventory auto-deducts when the kitchen starts cooking.</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="inventory-search-input"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search ingredients…"
                      className="h-9 w-56 pl-8"
                    />
                  </div>
                  <Dialog open={showAdd} onOpenChange={setShowAdd}>
                    <DialogTrigger asChild><Button data-testid="inventory-add-button" size="sm"><Plus className="mr-1 h-4 w-4" /> Add ingredient</Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>New ingredient</DialogTitle><DialogDescription>Track stock and set a low-stock alert threshold.</DialogDescription></DialogHeader>
                      <div className="grid gap-3">
                        <div className="space-y-1.5"><Label>Name</Label><Input data-testid="inventory-add-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5"><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="each, kg, L" /></div>
                          <div className="space-y-1.5"><Label>Cost / unit</Label><Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5"><Label>Stock qty</Label><Input data-testid="inventory-add-stock-input" type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} /></div>
                          <div className="space-y-1.5"><Label>Low-stock at</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Supplier (optional)</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
                      </div>
                      <DialogFooter><Button data-testid="inventory-add-submit-button" onClick={create} disabled={!form.name}>Create</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="p-2">Name</th><th className="p-2">Stock</th><th className="p-2">Threshold</th><th className="p-2">Cost</th><th className="p-2">Supplier</th><th className="p-2">Adjust</th><th className="p-2"></th></tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const s = search.trim().toLowerCase();
                      const filtered = ingredients.filter((i) => !s || (i.name || '').toLowerCase().includes(s) || (i.supplier || '').toLowerCase().includes(s));
                      // Low-stock first
                      const sorted = [...filtered].sort((a, b) => {
                        const aL = Number(a.stock_quantity) <= Number(a.low_stock_threshold) ? 0 : 1;
                        const bL = Number(b.stock_quantity) <= Number(b.low_stock_threshold) ? 0 : 1;
                        if (aL !== bL) return aL - bL;
                        return (a.name || '').localeCompare(b.name || '');
                      });
                      if (!sorted.length) return (<tr><td colSpan={7} className="p-6 text-center text-xs text-muted-foreground">No ingredients match your search.</td></tr>);
                      return sorted.map((ing) => {
                        const low = Number(ing.stock_quantity) <= Number(ing.low_stock_threshold);
                        return (
                          <tr key={ing.id} data-testid="inventory-ingredient-row" className={`border-t border-border ${low ? 'bg-amber-50/40' : ''}`}>
                            <td className="p-2 font-medium">{ing.name} <span className="text-xs text-muted-foreground">({ing.unit})</span></td>
                            <td className="p-2 tabular">{Number(ing.stock_quantity).toFixed(2)} {low && <Badge className="ml-2 border border-amber-300 bg-amber-50 text-amber-900">Low</Badge>}</td>
                            <td className="p-2 tabular text-muted-foreground">{ing.low_stock_threshold}</td>
                            <td className="p-2 tabular text-muted-foreground">${Number(ing.cost_per_unit || 0).toFixed(2)}</td>
                            <td className="p-2 text-xs text-muted-foreground truncate max-w-[160px]">{ing.supplier || '—'}</td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <Button data-testid="inventory-adjust-minus-button" size="icon" variant="secondary" className="h-7 w-7" onClick={() => adjust(ing.id, -1)}><Minus className="h-3 w-3" /></Button>
                                <Button data-testid="inventory-adjust-plus-button" size="icon" variant="secondary" className="h-7 w-7" onClick={() => adjust(ing.id, 1)}><Plus className="h-3 w-3" /></Button>
                                <Button size="icon" variant="secondary" className="h-7 w-7 text-xs" onClick={() => adjust(ing.id, 10)}>+10</Button>
                              </div>
                            </td>
                            <td className="p-2"><Button data-testid="inventory-ingredient-delete-button" size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(ing)}><Trash2 className="h-4 w-4" /></Button></td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="dishes">
            <DishesPanel menuItems={menuItems} categories={categories} reload={load} />
          </TabsContent>
          <TabsContent value="recipes">
            <RecipesPanel menuItems={menuItems} categories={categories} ingredients={ingredients} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Ingredient delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="inventory-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This ingredient will be removed from inventory. Any recipes referencing it will keep the reference but auto-deduction will skip it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="inventory-delete-confirm-button" onClick={doDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Dishes panel ----------
function DishesPanel({ menuItems, categories, reload }) {
  const [filter, setFilter] = useState('signature'); // 'signature' | 'all'
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  // Add-dish dialog state. Kept locally so the dialog can be reset cleanly
  // between attempts. `signature` is a tag (not a column) so we mirror the
  // existing filter logic when persisting.
  const [showAdd, setShowAdd] = useState(false);
  const [addDraft, setAddDraft] = useState(null);
  const [creating, setCreating] = useState(false);

  const blankDraft = React.useCallback(() => ({
    name: '',
    category_id: categories[0]?.id || '',
    description: '',
    price: '',
    image_url: '',
    prep_time_minutes: 15,
    is_available: true,
    is_signature: false,
  }), [categories]);

  const openAdd = () => {
    if (!categories.length) {
      toast.error('Add at least one category first (in the Menu admin tab).');
      return;
    }
    setAddDraft(blankDraft());
    setShowAdd(true);
  };

  const createDish = async () => {
    if (!addDraft) return;
    const name = addDraft.name.trim();
    const price = Number(addDraft.price);
    if (!name) { toast.error('Name is required'); return; }
    if (!addDraft.category_id) { toast.error('Category is required'); return; }
    if (!(price > 0)) { toast.error('Price must be greater than 0'); return; }
    setCreating(true);
    try {
      const tags = addDraft.is_signature ? ['signature'] : [];
      await api.post('/menu/items', {
        name,
        category_id: addDraft.category_id,
        description: addDraft.description?.trim() || null,
        price,
        image_url: addDraft.image_url?.trim() || null,
        prep_time_minutes: Number(addDraft.prep_time_minutes) || 15,
        is_available: !!addDraft.is_available,
        tags,
      });
      toast.success(`“${name}” added to the menu`);
      setShowAdd(false);
      setAddDraft(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create dish');
    } finally { setCreating(false); }
  };

  const catById = React.useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const visible = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    return menuItems.filter((m) => {
      if (filter === 'signature') {
        const tags = (m.tags || []).map((t) => (t || '').toLowerCase());
        if (!tags.includes('signature')) return false;
      }
      if (s && !(m.name || '').toLowerCase().includes(s)) return false;
      return true;
    });
  }, [menuItems, filter, search]);

  const grouped = React.useMemo(() => {
    const out = {};
    for (const m of visible) {
      const cat = m.category_id ? (catById[m.category_id]?.name || 'Uncategorized') : 'Uncategorized';
      (out[cat] = out[cat] || []).push(m);
    }
    return out;
  }, [visible, catById]);

  const saveEdit = async () => {
    if (!editItem) return;
    if (!editItem.name?.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await api.patch(`/menu/items/${editItem.id}`, {
        name: editItem.name.trim(),
        image_url: editItem.image_url?.trim() || null,
        description: editItem.description ?? null,
        price: Number(editItem.price) || 0,
      });
      toast.success('Dish updated');
      setEditItem(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/menu/items/${confirmDelete.id}`);
      toast.success('Dish removed');
      setConfirmDelete(null);
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2 text-base"><ChefHat className="h-5 w-5 text-primary" /> Dishes</CardTitle>
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {[
                { k: 'signature', label: 'Signature only' },
                { k: 'all', label: 'All' },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`dishes-filter-${opt.k}`}
                  onClick={() => setFilter(opt.k)}
                  className={`px-3 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${filter === opt.k ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
                >{opt.label}</button>
              ))}
            </div>
            <Button data-testid="dishes-add-button" size="sm" className="ml-1" onClick={openAdd}>
              <Plus className="mr-1 h-4 w-4" /> Add dish
            </Button>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="dishes-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dishes…" className="h-9 w-56 pl-8" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!visible.length && <p className="py-6 text-center text-sm text-muted-foreground">No dishes match your filter.</p>}
          {Object.entries(grouped).map(([catName, items]) => (
            <div key={catName} className="space-y-1.5">
              <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{catName}</p>
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                {items.map((m) => (
                  <li key={m.id} data-testid="dishes-row" className="flex items-center gap-3 bg-card px-3 py-2.5">
                    {m.image_url ? (
                      <img src={m.image_url} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-muted-foreground">${Number(m.price || 0).toFixed(2)} · {m.is_available ? 'Available' : <span className="text-rose-700">Disabled</span>}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button data-testid="dishes-edit-button" size="sm" variant="secondary" onClick={() => setEditItem({ ...m })}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button data-testid="dishes-delete-button" size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(m)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Add dish dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { if (!o) { setShowAdd(false); setAddDraft(null); } }}>
        <DialogContent data-testid="dishes-add-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a new dish</DialogTitle>
            <DialogDescription>Create a menu item your team can order from POS, iPad and the customer app.</DialogDescription>
          </DialogHeader>
          {addDraft && (
            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label>Name *</Label>
                <Input
                  data-testid="dishes-add-name-input"
                  autoFocus
                  value={addDraft.name}
                  onChange={(e) => setAddDraft({ ...addDraft, name: e.target.value })}
                  placeholder="e.g. Char-Grilled Ribeye"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Category *</Label>
                  <select
                    data-testid="dishes-add-category-select"
                    value={addDraft.category_id}
                    onChange={(e) => setAddDraft({ ...addDraft, category_id: e.target.value })}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Price *</Label>
                  <Input
                    data-testid="dishes-add-price-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={addDraft.price}
                    onChange={(e) => setAddDraft({ ...addDraft, price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  data-testid="dishes-add-description-input"
                  rows={2}
                  value={addDraft.description}
                  onChange={(e) => setAddDraft({ ...addDraft, description: e.target.value })}
                  placeholder="Short summary shown on menus and receipts."
                />
              </div>
              <div className="space-y-1.5">
                <Label>Image (URL or upload)</Label>
                <ImageUploader
                  value={addDraft.image_url || ''}
                  onChange={(v) => setAddDraft({ ...addDraft, image_url: v })}
                  kind="menu"
                  placeholder="https://… or click Upload →"
                  testIdPrefix="dishes-add-image"
                  previewHeight="h-28"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Prep time (min)</Label>
                  <Input
                    data-testid="dishes-add-preptime-input"
                    type="number"
                    min="1"
                    value={addDraft.prep_time_minutes}
                    onChange={(e) => setAddDraft({ ...addDraft, prep_time_minutes: e.target.value })}
                  />
                </div>
                <div className="flex flex-col justify-end gap-3 pb-1">
                  <label className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-sm">
                    <span>Available</span>
                    <Switch
                      data-testid="dishes-add-available-switch"
                      checked={!!addDraft.is_available}
                      onCheckedChange={(v) => setAddDraft({ ...addDraft, is_available: !!v })}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-sm">
                    <span>Signature dish</span>
                    <Switch
                      data-testid="dishes-add-signature-switch"
                      checked={!!addDraft.is_signature}
                      onCheckedChange={(v) => setAddDraft({ ...addDraft, is_signature: !!v })}
                    />
                  </label>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                After creating, set the recipe (ingredients used) from the <span className="font-medium">Recipes</span> tab so stock auto-decrements when this dish is sold.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowAdd(false); setAddDraft(null); }} disabled={creating}>Cancel</Button>
            <Button data-testid="dishes-add-save-button" onClick={createDish} disabled={creating}>
              {creating ? 'Creating…' : 'Create dish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dish dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit dish</DialogTitle><DialogDescription>Update the name, image, and price.</DialogDescription></DialogHeader>
          {editItem && (
            <div className="grid gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="dishes-edit-name-input" value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Image (URL or upload)</Label>
                <ImageUploader
                  value={editItem.image_url || ''}
                  onChange={(v) => setEditItem({ ...editItem, image_url: v })}
                  kind="menu"
                  placeholder="https://… or click Upload →"
                  testIdPrefix="dishes-edit-image"
                  previewHeight="h-28"
                />
              </div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={editItem.description || ''} onChange={(e) => setEditItem({ ...editItem, description: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Price</Label><Input type="number" step="0.01" value={editItem.price} onChange={(e) => setEditItem({ ...editItem, price: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="dishes-edit-save-button" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="dishes-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The dish will be marked unavailable and hidden from menus and POS. Past orders remain linked. You can re-enable from the admin Menu tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="dishes-delete-confirm-button" onClick={doDelete}>Remove dish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RecipesPanel({ menuItems, categories, ingredients }) {
  // Load all recipes in one shot so we can show coverage per menu item
  const [recipesByItem, setRecipesByItem] = useState({}); // { menu_item_id: { entries: [...] } }
  const [expandedId, setExpandedId] = useState(null);
  const [draftEntries, setDraftEntries] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | set | missing
  const [loadingRecipe, setLoadingRecipe] = useState(false);

  // Bulk-load all recipes for menu items so we can show coverage badges
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      for (const m of menuItems) {
        try {
          const { data } = await api.get(`/inventory/recipes/${m.id}`);
          out[m.id] = data || null;
        } catch (e) { out[m.id] = null; }
      }
      if (!cancelled) setRecipesByItem(out);
    })();
    return () => { cancelled = true; };
  }, [menuItems]);

  const loadRecipe = async (itemId) => {
    setLoadingRecipe(true);
    try {
      const { data } = await api.get(`/inventory/recipes/${itemId}`);
      setDraftEntries(data?.entries || []);
    } catch (e) { setDraftEntries([]); }
    finally { setLoadingRecipe(false); }
  };

  const toggleExpand = (itemId) => {
    if (expandedId === itemId) {
      setExpandedId(null);
      setDraftEntries([]);
      return;
    }
    setExpandedId(itemId);
    loadRecipe(itemId);
  };

  const addRow = () => setDraftEntries((p) => [...p, { ingredient_id: '', quantity_per_unit: 1 }]);
  const setRow = (i, k, v) => setDraftEntries((p) => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRow = (i) => setDraftEntries((p) => p.filter((_, idx) => idx !== i));

  const save = async (itemId) => {
    setSavingId(itemId);
    try {
      const clean = draftEntries.filter((e) => e.ingredient_id && Number(e.quantity_per_unit) > 0);
      await api.put('/inventory/recipes', {
        menu_item_id: itemId,
        entries: clean.map((e) => ({ ingredient_id: e.ingredient_id, quantity_per_unit: Number(e.quantity_per_unit) })),
      });
      toast.success('Recipe saved');
      // refresh local cache
      setRecipesByItem((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), entries: clean } }));
    } catch (e) { toast.error('Failed to save recipe'); }
    finally { setSavingId(null); }
  };

  // Group filtered items by category for navigation
  const ingById = React.useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const catById = React.useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const visibleItems = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    return menuItems.filter((m) => {
      const r = recipesByItem[m.id];
      const hasRecipe = !!(r && r.entries && r.entries.length);
      if (filterStatus === 'set' && !hasRecipe) return false;
      if (filterStatus === 'missing' && hasRecipe) return false;
      if (s && !(m.name || '').toLowerCase().includes(s)) return false;
      return true;
    });
  }, [menuItems, recipesByItem, search, filterStatus]);

  const grouped = React.useMemo(() => {
    const out = {};
    for (const m of visibleItems) {
      const cat = m.category_id ? (catById[m.category_id]?.name || 'Uncategorized') : 'Uncategorized';
      (out[cat] = out[cat] || []).push(m);
    }
    return out;
  }, [visibleItems, catById]);

  // Stats
  const stats = React.useMemo(() => {
    let set = 0;
    for (const m of menuItems) {
      const r = recipesByItem[m.id];
      if (r && r.entries && r.entries.length) set += 1;
    }
    return { total: menuItems.length, set, missing: menuItems.length - set };
  }, [menuItems, recipesByItem]);

  return (
    <div className="space-y-3">
      {/* How it works */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex items-start gap-3 p-4 text-sm">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="space-y-1 text-foreground">
            <p className="font-semibold">How recipes work</p>
            <p className="text-muted-foreground">
              A recipe links a menu item to the ingredients it consumes. When the kitchen marks an order as <strong>preparing</strong>, each
              ingredient is automatically deducted from your inventory based on its quantity per dish — so stock stays accurate without manual work.
            </p>
            <p className="text-xs text-muted-foreground">
              <strong>Tip:</strong> menu items without a recipe still sell — they just won't auto-decrement stock.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Coverage KPIs + filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-base"><ChefHat className="h-5 w-5 text-primary" /> Recipes</CardTitle>
            <Badge data-testid="recipes-coverage-badge" variant="secondary" className="font-display tabular">
              {stats.set} / {stats.total} linked
            </Badge>
            {stats.missing > 0 && <Badge className="border border-amber-300 bg-amber-50 text-amber-900">{stats.missing} missing recipe</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {[
                { k: 'all', label: 'All' },
                { k: 'set', label: 'With recipe' },
                { k: 'missing', label: 'Missing' },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`recipes-filter-${opt.k}`}
                  onClick={() => setFilterStatus(opt.k)}
                  className={`px-3 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${filterStatus === opt.k ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
                >{opt.label}</button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="recipes-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dishes…"
                className="h-9 w-56 pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!visibleItems.length && (
            <p className="py-6 text-center text-sm text-muted-foreground">No dishes match your filter.</p>
          )}
          {Object.entries(grouped).map(([catName, items]) => (
            <div key={catName} className="space-y-1.5">
              <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{catName}</p>
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                {items.map((m) => {
                  const recipe = recipesByItem[m.id];
                  const hasRecipe = !!(recipe && recipe.entries && recipe.entries.length);
                  const ingCount = hasRecipe ? recipe.entries.length : 0;
                  const isOpen = expandedId === m.id;
                  return (
                    <li key={m.id} data-testid="recipes-item-row" className="bg-card">
                      <div className="flex items-center gap-3 px-3 py-2.5">
                        <img src={m.image_url} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{m.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {hasRecipe ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <CheckCircle2 className="h-3 w-3" /> Recipe set · {ingCount} ingredient{ingCount === 1 ? '' : 's'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <AlertCircle className="h-3 w-3" /> No recipe linked
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="hidden items-center gap-1 sm:flex">
                          {hasRecipe && recipe.entries.slice(0, 3).map((e, idx) => (
                            <Badge key={idx} variant="secondary" className="text-[10px]">
                              {ingById[e.ingredient_id]?.name || '—'} ×{e.quantity_per_unit}
                            </Badge>
                          ))}
                          {hasRecipe && recipe.entries.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{recipe.entries.length - 3}</span>
                          )}
                        </div>
                        <Button
                          data-testid={hasRecipe ? 'recipes-edit-button' : 'recipes-add-button'}
                          size="sm"
                          variant={hasRecipe ? 'secondary' : 'default'}
                          onClick={() => toggleExpand(m.id)}
                          className="shrink-0"
                        >
                          {hasRecipe
                            ? (isOpen ? <>Close</> : <><Pencil className="mr-1 h-3.5 w-3.5" /> Edit recipe</>)
                            : (isOpen ? <>Close</> : <><Plus className="mr-1 h-3.5 w-3.5" /> Add recipe</>)
                          }
                        </Button>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                      {isOpen && (
                        <div className="border-t border-border bg-muted/20 p-4" data-testid="recipes-item-expanded">
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs text-muted-foreground">
                              Each ingredient is deducted by <strong>quantity × number ordered</strong> when the kitchen starts cooking.
                            </p>
                            <div className="flex items-center gap-1.5">
                              <Button data-testid="recipes-add-row-button" size="sm" variant="secondary" onClick={addRow} disabled={loadingRecipe}>
                                <Plus className="mr-1 h-4 w-4" /> Add ingredient
                              </Button>
                              <Button
                                data-testid="recipes-save-button"
                                size="sm"
                                onClick={() => save(m.id)}
                                disabled={loadingRecipe || savingId === m.id}
                              >
                                <Save className="mr-1 h-4 w-4" /> {savingId === m.id ? 'Saving…' : 'Save recipe'}
                              </Button>
                            </div>
                          </div>
                          {loadingRecipe ? (
                            <p className="py-4 text-center text-sm text-muted-foreground">Loading recipe…</p>
                          ) : (
                            <div className="overflow-x-auto rounded border border-border bg-card">
                              <table className="w-full min-w-[480px] text-sm">
                                <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                                  <tr>
                                    <th className="p-2">Ingredient</th>
                                    <th className="w-44 p-2">Qty per dish</th>
                                    <th className="w-12 p-2"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {draftEntries.map((row, i) => (
                                    <tr key={i} className="border-t border-border">
                                      <td className="p-2">
                                        <select
                                          data-testid="recipes-ingredient-select"
                                          value={row.ingredient_id}
                                          onChange={(e) => setRow(i, 'ingredient_id', e.target.value)}
                                          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                        >
                                          <option value="">Select…</option>
                                          {ingredients.map((ing) => (
                                            <option key={ing.id} value={ing.id}>
                                              {ing.name} ({ing.unit})
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-2">
                                        <div className="flex items-center gap-1">
                                          <Input
                                            data-testid="recipes-qty-input"
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={row.quantity_per_unit}
                                            onChange={(e) => setRow(i, 'quantity_per_unit', e.target.value)}
                                            className="h-9 w-24"
                                          />
                                          <span className="text-xs text-muted-foreground">{ingById[row.ingredient_id]?.unit || ''}</span>
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <Button data-testid="recipes-remove-row-button" size="icon" variant="ghost" className="text-destructive" onClick={() => removeRow(i)}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                  {!draftEntries.length && (
                                    <tr>
                                      <td colSpan={3} className="p-4 text-center text-xs text-muted-foreground">
                                        No ingredients linked yet. Click "Add ingredient" above.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
